import React, { FC, Fragment, useState } from 'react';
import { Button } from 'antd';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import CustomModal from 'src/components/CustomModal';
import { IRootReducers } from 'src/reducers';
import {
  ButtonStatusEnum,
  ChallengeTypeEnum,
  ExperienceDetailStatusEnum,
} from 'src/shared/enums';
import {
  IChallengeCheckoutBookingTimify,
  IChallengeResourceRender,
  IExperienceDetailWrapper,
} from 'src/shared/models';
import {
  addItemToLocalStorage,
  checkUserResourcePoints,
  customFormatMessage,
  removeItemFromLocalStorage,
} from 'utils';
import Link from 'src/components/Link';
import routePaths from 'config/routes';

interface OwnProps extends IChallengeResourceRender {
  config: IChallengeCheckoutBookingTimify;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ChallengeCheckoutBookingTimify: FC<Props> = ({
  challenge,
  config,
  currentUserPoints,
  user,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const userPoints = currentUserPoints || 0;
  const hasUserEnoughPoints = checkUserResourcePoints(
    userPoints,
    config.points,
    challenge?.challengeType
  );
  const isExperience =
    challenge?.challengeType === ChallengeTypeEnum.EXPERIENCE && config;

  const { formatMessage } = useIntl();
  const { points, buttonLabel, text, bookingId } = config;

  const labelPoints = Math.abs(config.points);

  const handleOpenModal = async () => {
    addItemToLocalStorage('idResource', config.idResource);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setError(null);
    setTimeout(() => {
      removeItemFromLocalStorage('idResource');
    }, 5000);
  };

  const wrapperConfig: IExperienceDetailWrapper = {
    title: text,
    points,
    status: ExperienceDetailStatusEnum.IN_PROGRESS,
  };

  if (config.isBlocked) {
    wrapperConfig.button = {
      status: ButtonStatusEnum.DISABLED,
      performAction: null,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.DISABLED;
  }

  if (!config.isChallengeInDate) {
    wrapperConfig.button = {
      status: ButtonStatusEnum.HIDDEN,
      performAction: null,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.HIDDEN;
  }
  const preSubmitModalContent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0em 1em',
        justifyContent: 'center',
        alignItems: 'center',
        height: '90%',
      }}
    >
      <iframe
        className="timifyIframe"
        title="timify booking"
        src={`https://book.timify.com?accountId=${bookingId}&fullscreen=true&hideCloseButton=1&&guestFirstName=${
          user.salesforce.userFirstname ? user.salesforce.userFirstname : ''
        }&guestLastName=${
          user.salesforce.userSurname ? user.salesforce.userSurname : ''
        }&guestEmail=${
          user.salesforce.userMail ? user.salesforce.userMail : ''
        }&guestPhone=${
          user.salesforce.userPhone
            ? user.salesforce.userPhone.split('-').pop()
            : ''
        }`}
      />
    </div>
  );

  const checkoutButtonBalance = () => {
    const renderBalance =
      challenge?.challengePoints || challenge?.challengePoints === 0;
    if (!renderBalance) return <Fragment />;

    if (hasUserEnoughPoints)
      return (
        <div className="experience-checkout-button__points">
          <div className="experience-checkout-button__points-balance">
            <p className="regular-body experience-checkout-button__points-balance__text">
              {customFormatMessage(
                'page.experience.checkout.user-balance-has-points'
              )}
            </p>
          </div>
        </div>
      );

    return (
      <div className="experience-checkout-button__points">
        <div className="experience-checkout-button__points-balance">
          <p className="regular-body experience-checkout-button__points-balance__text">
            {customFormatMessage(
              'page.experience.checkout.user-balance-lacks-{points}',
              {
                points: Number(labelPoints - userPoints),
              }
            )}
          </p>
        </div>

        <div className="experience-checkout-button__points-link">
          <Link href={routePaths.PAGES.SCAN.SCAN_INDEX}>
            <Button className="experience-checkout-button__points-link--button">
              {formatMessage({
                id: 'page.experience.checkout.user-balance-get-more-points',
              })}
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  if (!isExperience) return <Fragment />;

  return (
    <>
      <div className="show_on_breakpoint-sm experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow">
        {
          <div>
            <span>{text ? text : ''}</span>
            {checkoutButtonBalance()}
            <Button
              disabled={
                config.isBlocked || !hasUserEnoughPoints || user.blacklisted
              }
              className="btn-primary btn-primary--black experience-checkout-button experience-checkout-button__single-product"
              onClick={handleOpenModal}
            >
              {buttonLabel}
            </Button>
          </div>
        }
      </div>
      <div className="not_show_on_breakpoint-sm experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow">
        <div className="container experience-checkout-button__container">
          <div className="experience-checkout-button__container-timify">
            <img
              className="experience-checkout-button__container--image-timify"
              src={challenge.cardImage}
              alt="checkout-img"
            ></img>
            <div className="experience-checkout-button__container--title-timify">
              <span>{text ? text : ''}</span>
            </div>
          </div>
          <div>
            {checkoutButtonBalance()}
            {
              <Button
                disabled={
                  config.isBlocked || !hasUserEnoughPoints || config.isCompleted
                }
                className="btn-primary btn-primary--black experience-checkout-button experience-checkout-button__single-product"
                onClick={handleOpenModal}
              >
                {buttonLabel}
              </Button>
            }
          </div>
        </div>
      </div>

      <CustomModal
        {...{
          visible: modalVisible,
          loading,
          error,
          content: !config.isCompleted && preSubmitModalContent,
          onCancel: handleCloseModal,
          footer: null,
          showIcon: false,
          customClass: 'full-screen-mobile',
        }}
      />
    </>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    currentUserPoints: state.auth.userPoints,
    user: state.auth.user,
  };
};

export default connect(mapStateToProps)(ChallengeCheckoutBookingTimify);
